import { styled } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import SplitscreenOutlinedIcon from '@mui/icons-material/SplitscreenOutlined';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import WebhookIcon from '@mui/icons-material/Webhook';
import DataObjectIcon from '@mui/icons-material/DataObject';

const allDrawerItems = [
  {
    label: 'Agents',
    path: '/app/agents',
    icon: SupportAgentOutlinedIcon,
    selectedIcon: SupportAgentIcon,
  },
  {
    label: 'Tasks',
    path: '/app/tasks',
    icon: SplitscreenOutlinedIcon,
    selectedIcon: SplitscreenIcon,
  },
  {
    label: 'Document flows',
    path: '/app/policies',
    icon: DocumentScannerOutlinedIcon,
    selectedIcon: DocumentScannerIcon,
  },
  {
    label: 'Functions',
    path: '/app/functions',
    icon: DataObjectIcon,
  },
  {
    label: 'Webhooks',
    path: '/app/webhooks',
    icon: WebhookIcon,
  },
  {
    label: 'Storage',
    path: '/app/storage',
    icon: FolderOutlinedIcon,
    selectedIcon: FolderIcon,
  },
];

export const drawerItems = allDrawerItems.filter((item) => {
  if (item.label === 'Storage' && process.env.REACT_APP_STORAGE_TAB !== 'true') return false;
  if (item.label === 'Functions' && process.env.REACT_APP_FUNCTIONS_TAB !== 'true') return false;
  return true;
});

const drawerWidth = 240;
const navigationIconSize = 24;

export const getDrawerItemId = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

export const getNavigationItemIconSlotStyles = () => ({
  width: navigationIconSize,
  minWidth: navigationIconSize,
  maxWidth: navigationIconSize,
  height: navigationIconSize,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  justifySelf: 'center',
  flexShrink: 0,
  lineHeight: 0,
  m: 0,
  mr: 0,
  p: 0,
  fontSize: navigationIconSize,
  color: 'inherit',
  transition: 'color 0.2s ease, transform 0.2s ease',
});

export const navigationItemIconStyles = {
  display: 'block',
  fontSize: 'inherit',
};

const getSidebarPaperStyles = (theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    backgroundColor: isDark ? 'rgba(12, 18, 31, 0.96)' : 'var(--joy-palette-neutral-50, #f7f9fc)',
    backgroundImage: isDark
      ? 'radial-gradient(circle at top left, rgba(93, 141, 255, 0.24) 0%, rgba(93, 141, 255, 0) 32%), linear-gradient(180deg, rgba(13, 20, 35, 0.98) 0%, rgba(9, 14, 27, 0.98) 100%)'
      : 'radial-gradient(circle at top left, rgba(93, 141, 255, 0.18) 0%, rgba(93, 141, 255, 0) 32%), linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(243, 247, 255, 0.98) 22%, rgba(238, 243, 251, 1) 100%)',
    borderRight: `1px solid ${isDark ? 'rgba(125, 152, 207, 0.22)' : 'rgba(93, 141, 255, 0.22)'}`,
    boxShadow: isDark
      ? '12px 0 34px rgba(2, 6, 23, 0.42), inset -1px 0 0 rgba(255, 255, 255, 0.06)'
      : '10px 0 28px rgba(15, 23, 42, 0.08), inset -1px 0 0 rgba(255, 255, 255, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '8px 8px 8px 6px',
      borderRadius: '26px',
      background: isDark
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%)'
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.08) 100%)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.38)'}`,
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 12,
      bottom: 12,
      right: 0,
      width: '3px',
      borderRadius: '999px',
      background: isDark
        ? 'linear-gradient(180deg, rgba(128, 173, 255, 0.25) 0%, rgba(93, 141, 255, 0.78) 46%, rgba(128, 173, 255, 0.25) 100%)'
        : 'linear-gradient(180deg, rgba(128, 173, 255, 0.55) 0%, rgba(93, 141, 255, 0.95) 46%, rgba(128, 173, 255, 0.55) 100%)',
      boxShadow: isDark ? '1px 0 0 rgba(0, 0, 0, 0.24)' : '1px 0 0 rgba(15, 23, 42, 0.12)',
      pointerEvents: 'none',
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  };
};

const getSidebarContentTransition = (theme, isVisible) => {
  const enteringDuration = theme.transitions?.duration?.enteringScreen ?? 225;
  const leavingDuration = theme.transitions?.duration?.leavingScreen ?? 195;
  const easeOut = theme.transitions?.easing?.easeOut ?? 'cubic-bezier(0.0, 0, 0.2, 1)';
  const sharp = theme.transitions?.easing?.sharp ?? 'cubic-bezier(0.4, 0, 0.6, 1)';
  const duration = isVisible
    ? enteringDuration
    : leavingDuration;
  const easing = isVisible
    ? easeOut
    : sharp;

  return [
    `opacity ${duration}ms ${easing}`,
    `transform ${duration}ms ${easing}`,
    `max-width ${duration}ms ${easing}`,
  ].join(', ');
};

const getAnimatedSidebarCopyBaseStyles = (theme, isVisible, expandedMaxWidth) => ({
  minWidth: 0,
  maxWidth: isVisible ? expandedMaxWidth : 0,
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(-8px)',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  transition: getSidebarContentTransition(theme, isVisible),
  pointerEvents: isVisible ? 'auto' : 'none',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    transform: 'none',
  },
});

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open ? openedMixin(theme) : closedMixin(theme)),
  '& .MuiDrawer-paper': {
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    ...getSidebarPaperStyles(theme),
  },
}));

export const dividerStyles = (theme) => ({
  mx: 1.5,
  borderColor: theme.palette.mode === 'dark'
    ? 'rgba(125, 152, 207, 0.16)'
    : 'rgba(93, 141, 255, 0.18)',
});

export const toggleButtonStyles = {
  color: 'text.secondary',
  borderRadius: 'md',
  '&:hover': {
    backgroundColor: 'background.level1',
    color: 'text.primary',
  },
  '&:focus-visible': {
    backgroundColor: 'primary.softBg',
    boxShadow: '0 0 0 3px var(--joy-palette-primary-softBg, rgba(93, 141, 255, 0.18))',
  },
};

export const logoButtonStyles = {
  width: 38,
  height: 38,
  borderRadius: 'lg',
  backgroundColor: 'primary.softBg',
  border: '1px solid',
  borderColor: 'primary.outlinedBorder',
  boxShadow: 'sm',
  '&:hover, &:active, &:focus-visible': {
    backgroundColor: 'primary.softHoverBg',
    boxShadow: 'sm',
  },
};

export const getNavigationItemStyles = (open) => ({
  minHeight: 44,
  px: open ? 1.5 : 0,
  py: 1,
  borderRadius: 'md',
  width: '100%',
  ...(open
    ? {
        display: 'grid',
        gridTemplateColumns: `${navigationIconSize}px minmax(0, 1fr)`,
        columnGap: 1.5,
        justifyContent: 'stretch',
        justifyItems: 'stretch',
      }
    : {
        display: 'flex',
        justifyContent: 'center',
        '--ListItem-paddingLeft': '0px',
        '--ListItem-paddingRight': '0px',
      }),
  alignItems: 'center',
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  transition:
    'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  '& .MuiTypography-root': {
    color: 'inherit',
  },
  '&:hover': {
    backgroundColor: 'background.level1',
    borderColor: 'divider',
    color: 'text.primary',
  },
  '&:focus-visible': {
    backgroundColor: 'primary.softBg',
    borderColor: 'primary.outlinedBorder',
    boxShadow: '0 0 0 3px var(--joy-palette-primary-softBg, rgba(93, 141, 255, 0.18))',
  },
  '&.Mui-selected': {
    backgroundColor: 'primary.softBg',
    borderColor: 'primary.outlinedBorder',
    color: 'primary.softColor',
    boxShadow: 'sm',
  },
  '&.Mui-selected .MuiListItemIcon-root': {
    color: 'primary.softColor',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'primary.softHoverBg',
    borderColor: 'primary.outlinedBorder',
  },
});

export const getFooterStyles = () => ({
  mt: 'auto',
  mx: 1.5,
  mb: 1.5,
  p: 1,
  borderRadius: 'xl',
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme) => (
    theme.palette.mode === 'dark'
      ? 'rgba(15, 23, 42, 0.72)'
      : 'rgba(255, 255, 255, 0.78)'
  ),
  boxShadow: (theme) => (
    theme.palette.mode === 'dark'
      ? '0 14px 30px rgba(2, 6, 23, 0.34)'
      : '0 10px 24px rgba(15, 23, 42, 0.08)'
  ),
  backdropFilter: 'blur(16px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
});

export const getFooterControlRowStyles = (open) => ({
  display: 'flex',
  justifyContent: open ? 'flex-end' : 'center',
});

export const footerColorToggleStyles = {
  color: 'text.secondary',
  borderColor: 'divider',
  backgroundColor: 'background.surface',
  '&:hover': {
    backgroundColor: 'background.level1',
    color: 'text.primary',
  },
  '&:focus-visible': {
    boxShadow: '0 0 0 3px var(--joy-palette-primary-softBg, rgba(93, 141, 255, 0.18))',
  },
};

export const getFooterContentStyles = (open) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'space-between' : 'center',
  flexDirection: open ? 'row' : 'column',
  gap: 1,
});

export const getProfileSectionStyles = (open) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  flex: open ? 1 : '0 0 auto',
});

export const getAnimatedSidebarGroupStyles = (isVisible, expandedMaxWidth) => (theme) => ({
  ...getAnimatedSidebarCopyBaseStyles(theme, isVisible, expandedMaxWidth),
  display: 'flex',
  alignItems: 'center',
  gap: 1,
});

export const getAnimatedSidebarTextStyles = (isVisible, expandedMaxWidth) => (theme) => ({
  ...getAnimatedSidebarCopyBaseStyles(theme, isVisible, expandedMaxWidth),
});

export const getProfileButtonStyles = (isSelected) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  color: isSelected ? 'primary.softColor' : 'text.secondary',
  backgroundColor: isSelected ? 'primary.softBg' : 'transparent',
  border: '1px solid',
  borderColor: isSelected ? 'primary.outlinedBorder' : 'transparent',
  transition:
    'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    backgroundColor: 'background.level1',
    borderColor: 'divider',
    color: 'text.primary',
  },
  '&:focus-visible': {
    backgroundColor: 'primary.softBg',
    borderColor: 'primary.outlinedBorder',
    boxShadow: '0 0 0 3px var(--joy-palette-primary-softBg, rgba(93, 141, 255, 0.18))',
  },
});

export const logoutButtonStyles = {
  width: 36,
  height: 36,
  borderRadius: '12px',
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'danger.softBg',
    color: 'danger.softColor',
  },
};

export const versionBadgeStyles = {
  color: 'text.tertiary',
  fontSize: '0.65rem',
  letterSpacing: '0.02em',
  textAlign: 'center',
  opacity: 0.7,
  userSelect: 'none',
  pt: 0.5,
};
